/**
 * Geocoding helper – resolves the user's position (via Google Maps Geolocation API)
 * into city, region and country using Google Maps Geocoding API.
 *
 * Both API keys are read from window.freedomeConfig.googleGeocodingApiKey,
 * which is injected server-side.
 */

// ─────────────────────────────────────────────
// Google Maps Geolocation (network-based, no browser prompt)
// ─────────────────────────────────────────────

/**
 * Resolves the device's coordinates by calling the Google Maps Geolocation API.
 * Uses IP address, WiFi access point data and cell tower data server-side —
 * no browser permission prompt is shown to the user.
 *
 * @returns {Promise<{latitude: number, longitude: number, accuracy: number}>}
 */
const getPositionFromGoogleGeolocation = async () => {
    const apiKey = window.freedomeConfig?.googleGeocodingApiKey;
    if (!apiKey) {
        throw new Error('Google Geolocation API key not configured');
    }

    const url = `https://www.googleapis.com/geolocation/v1/geolocate?key=${encodeURIComponent(apiKey)}`;

    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ considerIp: true }),
    });

    if (!response.ok) {
        throw new Error(`Google Geolocation request failed: ${response.status}`);
    }

    const data = await response.json();
    if (!data.location) {
        throw new Error('Google Geolocation returned no location data');
    }

    return {
        latitude: data.location.lat,
        longitude: data.location.lng,
        accuracy: data.accuracy ?? null,
    };
};

// ─────────────────────────────────────────────
// Google Maps Geocoding
// ─────────────────────────────────────────────

/**
 * Calls the Google Maps Geocoding API (reverse-geocode) and extracts
 * city, region, province and country from the response.
 *
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<{city: string|null, region: string|null, province: string|null, country: string|null}>}
 */
const reverseGeocode = async (latitude, longitude) => {
    const apiKey = window.freedomeConfig?.googleGeocodingApiKey;
    if (!apiKey) {
        throw new Error('Google Geocoding API key not configured');
    }

    const url =
        `https://maps.googleapis.com/maps/api/geocode/json` +
        `?latlng=${encodeURIComponent(latitude)},${encodeURIComponent(longitude)}` +
        `&result_type=${encodeURIComponent('locality|administrative_area_level_1|administrative_area_level_2')}` +
        `&key=${encodeURIComponent(apiKey)}`;

    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Geocoding request failed: ${response.status}`);
    }

    const data = await response.json();
    if (data.status !== 'OK' || !data.results || data.results.length === 0) {
        throw new Error(
            `Geocoding returned status: ${data.status}` +
            (data.error_message ? ` – ${data.error_message}` : '')
        );
    }

    // Walk through address_components of the best result to extract parts
    let city = null;
    let region = null;
    let province = null;
    let country = null;

    for (const result of data.results) {
        for (const component of result.address_components) {
            const types = component.types;

            if (!city && types.includes('locality')) {
                city = component.long_name;
            }

            if (!region && (types.includes('administrative_area_level_1'))) {
                region = component.long_name;
            }

            if (!province && (types.includes('administrative_area_level_2'))) {
                province = component.long_name;
            }

            if (!country && types.includes('country')) {
                // Braze setCountry() expects ISO-3166-1 alpha-2 (e.g. "IT"), which Google returns as short_name.
                country = component.short_name;
            }

            if (city && region && province && country) break;
        }
        if (city && region && province && country) break;
    }

    return { city, region, province, country };
};

// ─────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────

/**
 * Resolves the user's location via Google Maps Geolocation API + Google Geocoding.
 * Always returns a location object (never null). When geocoding fails or
 * doesn't return a country, the marketplace fallback (IT / ES) is used and
 * `countryIsGeocoded` is set to false so callers can avoid overwriting a
 * previously geocoded country.
 *
 * @returns {Promise<{city: string|null, region: string|null, province: string|null, country: string, countryIsGeocoded: boolean, latitude: number|null, longitude: number|null, accuracy: number|null}>}
 */
export const resolveUserLocation = async () => {
    const fallbackCountry = window.freedomeConfig.baseLocale === 'es' ? 'ES' : 'IT';

    try {
        const { latitude, longitude, accuracy } = await getPositionFromGoogleGeolocation();
        const location = await reverseGeocode(latitude, longitude);

        const countryIsGeocoded = !!location.country;
        if (!countryIsGeocoded) {
            location.country = fallbackCountry;
        }

        // console.log('[freedome][geocoding] Resolved location:', location);
        return { ...location, countryIsGeocoded, latitude, longitude, accuracy };
    } catch (error) {
        // console.warn('[freedome][geocoding] Could not resolve location:', error.message || error);
        return { city: null, region: null, province: null, country: fallbackCountry, countryIsGeocoded: false, latitude: null, longitude: null, accuracy: null };
    }
};
